import { useAppDispatch, useAppTranslation } from "@/utils";
import { Button, Card, Divider, Flex, Typography, useMessagedAsync, useMessagedAsyncFn } from "@payping/web-components";
import { bnplConsumerApis } from "@payping/api";
import Loading from "@/pages/loading";
import { dashboardReduxReducerActions } from "@/redux/actions/dashboard";
import { RetryGetOrderSteps } from "@/redux/reducers/dashboard";
import { CuToman } from "@payping/icons";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";
import PayExpiredOrder from "../../ExpiredOrder";
import PayUnAvailable from "../../Unavailable";
import PayGetOrderLayout from "../Layout";

interface PrePayProps {
  afterSuccess: () => void;
  trackingCode: string;
}

const PayGetOrderPrePay = (props: PrePayProps) => {
  const { t } = useAppTranslation();
  const dispatch = useAppDispatch();

  const quoteState = useMessagedAsync(
    async () => {
      try {
        const res = await bnplConsumerApis.getQuote(props.trackingCode);
        return res;
      } catch (e: any) {
        if (e.status === 400) {
          try {
            const parsedError = await e.json();
            throw parsedError;
          } catch (jsonError: any) {
            if (jsonError.status === 400) {
              throw jsonError;
            }
            throw e; // Fallback if parsing JSON fails
          }
        }
        throw e;
      }
    },
    [props.trackingCode],
    {
      success: false,
      error: true,
    },
  );

  const [prePayState, prePay] = useMessagedAsyncFn(async () => {
    try {
      const res = await bnplConsumerApis.prePaymentPay({
        trackingCode: props.trackingCode,
      });
      return res;
    } catch (e: any) {
      if (e.status === 400) {
        try {
          const parsedError = await e.json();
          throw parsedError;
        } catch (jsonError: any) {
          if (jsonError.status === 400) {
            throw jsonError;
          }
          throw e; // Fallback if parsing JSON fails
        }
      }
      throw e;
    }
  }, [props.trackingCode]);

  if (quoteState.loading || quoteState.error?.name === "AuthError") {
    return (
      <PayGetOrderLayout showHeaders={false}>
        <Loading />
      </PayGetOrderLayout>
    );
  }

  // expired order
  if ((quoteState.error as any)?.key === 109 || (prePayState.error as any)?.key === 109) {
    return (
      <PayGetOrderLayout showHeaders={false}>
        <PayExpiredOrder />
      </PayGetOrderLayout>
    );
  }

  if (quoteState.error) {
    return (
      <PayGetOrderLayout showHeaders={false}>
        <PayUnAvailable
          onRetry={() => {
            dispatch(dashboardReduxReducerActions.setRetryGetOrder(RetryGetOrderSteps.startGetOrder));
          }}
        />
      </PayGetOrderLayout>
    );
  }

  const handleClickPrePay = async () => {
    const res = await prePay();
    if (res.url) {
      window.location.replace(res.url);
    }
  };

  return (
    <PayGetOrderLayout showHeaders>
      <Flex vertical className="tw:h-full tw:w-full" gap={"large"}>
        <Flex vertical className="tw:w-full tw:px-4! tw:pb-4! tw:pt-6!" gap={"middle"}>
          <Typography.Title level={5} className="tw:m-0! tw:font-semibold">
            {t("pay.getOrder.steps.prePay.card.title")}
          </Typography.Title>
          <Card size="small">
            <Flex gap={"middle"} vertical>
              <Flex justify="space-between">
                <Typography.Text type="secondary">{t("pay.getOrder.steps.prePay.card.prePayBuy")}</Typography.Text>
                <Flex align="center">
                  <Typography.Text>{`${formatLocalizedNumber({ value: quoteState.value?.prepayment ?? 0 })}`}</Typography.Text>
                  <CuToman width={24} height={24} />
                </Flex>
              </Flex>
              <Flex justify="space-between">
                <Typography.Text type="secondary">
                  {t("pay.getOrder.steps.prePay.card.infrastructureCost")}
                </Typography.Text>

                <Flex align="center">
                  <Typography.Text>{`${formatLocalizedNumber({ value: quoteState.value?.infraCost ?? 0 })}`}</Typography.Text>
                  <CuToman width={24} height={24} />
                </Flex>
              </Flex>
            </Flex>
          </Card>
        </Flex>
        <Flex vertical className="tw:mt-auto tw:w-full">
          <Divider className="tw:m-0!" />
          <Flex className="tw:w-full tw:px-4! tw:py-2!" align="center" justify="space-between">
            <Flex vertical>
              <Typography.Text type="secondary">{t("common.payableAmount")}</Typography.Text>

              <Flex align="center">
                <Typography.Text>
                  {formatLocalizedNumber({
                    value: (quoteState.value?.prepayment ?? 0) + (quoteState.value?.infraCost ?? 0),
                  })}
                </Typography.Text>
                <CuToman width={24} height={24} />
              </Flex>
            </Flex>
            <Button
              onClick={async () => {
                await handleClickPrePay();
              }}
              className="tw:px-10!"
              type="primary"
              loading={prePayState.loading}
            >
              {t("common.pay")}
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </PayGetOrderLayout>
  );
};

export default PayGetOrderPrePay;
